import { useGetPsiContractingProfile } from '@/lib/api/psi-profile';
import { ReactNode, useMemo } from 'react';
import {
  AccountCircle,
  AutoAwesome,
  Checklist,
  Description,
  FactCheck,
  Group,
  HomeWork,
  Insights,
  Search,
  Settings,
  TrendingUp,
} from '@mui/icons-material';
import { useGetMe, useGetQuickGuideStatus } from '@/lib/api/me';

export interface QuickGuideItem {
  id: string;
  title: string;
  description: string;
  href: string;
  completed: boolean;
  icon: ReactNode;
  ctaLabel: string;
}

export function useQuickGuideItems() {
  const { data: user } = useGetMe();
  const { data: contracting } = useGetPsiContractingProfile(
    user?.role === 'psi' && user.onboardingCompleted
  );
  const { data: status } = useGetQuickGuideStatus({ enabled: Boolean(user) });

  return useMemo<QuickGuideItem[]>(() => {
    if (!user || !status) return [];

    if (user.role === 'investor') {
      return [
        {
          id: 'investments',
          title: 'Registra tus inversiones',
          description: 'Sube tus inversiones para seguir rentabilidad, gastos y evolución.',
          href: status.hasInvestments ? '/inversiones' : '/inversiones/nueva',
          completed: status.hasInvestments,
          icon: <HomeWork />,
          ctaLabel: status.hasInvestments ? 'Ver inversiones' : 'Registrar',
        },
        {
          id: 'investment_assistant',
          title: 'Pregunta a la IA',
          description: 'Abre una inversión y usa el asistente para analizar datos y dudas.',
          href: '/inversiones',
          completed: status.hasUsedAssistant,
          icon: <AutoAwesome />,
          ctaLabel: 'Ir a inversiones',
        },
        {
          id: 'tools',
          title: 'Usa herramientas de análisis',
          description: 'Accede a Catastro y fuentes oficiales para estudiar zonas e inmuebles.',
          href: '/herramientas',
          completed: status.hasUsedTools,
          icon: <Insights />,
          ctaLabel: 'Abrir herramientas',
        },
        {
          id: 'opportunities',
          title: 'Explora oportunidades',
          description: 'Revisa oportunidades disponibles y filtra por ubicación o tipo.',
          href: '/oportunidades',
          completed: status.hasViewedOpportunities,
          icon: <Search />,
          ctaLabel: 'Explorar',
        },
        {
          id: 'preferences',
          title: 'Configura tus preferencias',
          description: 'Define zonas y tipos de inversión para ver oportunidades más relevantes.',
          href: '/oportunidades',
          completed: status.hasPreferences,
          icon: <Settings />,
          ctaLabel: 'Configurar',
        },
        {
          id: 'suitability',
          title: 'Haz el test de idoneidad',
          description: 'Es necesario para poder invertir y mostrar interés en oportunidades.',
          href: '/test-idoneidad',
          completed: user.suitabilityTestStatus === 'PASSED',
          icon: <FactCheck />,
          ctaLabel: 'Ver test',
        },
        {
          id: 'profile',
          title: 'Completa tu perfil',
          description: 'Añade tus datos para operar con más confianza dentro de la plataforma.',
          href: '/perfil',
          completed: user.profileCompleted,
          icon: <AccountCircle />,
          ctaLabel: 'Editar perfil',
        },
      ];
    }

    return [
      {
        id: 'create_opportunity',
        title: 'Publica una oportunidad',
        description: 'Crea oportunidades con datos de inmueble, rentabilidad e imágenes.',
        href: '/oportunidades/nuevo',
        completed: status.hasPublishedOpportunity,
        icon: <HomeWork />,
        ctaLabel: 'Crear oportunidad',
      },
      {
        id: 'publish_opportunity',
        title: 'Lleva una oportunidad a revisión',
        description: 'Edita, previsualiza y envía oportunidades para que puedan publicarse.',
        href: '/oportunidades',
        completed: status.hasPublishedOpportunity,
        icon: <TrendingUp />,
        ctaLabel: 'Ver oportunidades',
      },
      {
        id: 'manage_interests',
        title: 'Gestiona inversores interesados',
        description: 'Revisa interesados, selecciona inversor y avanza hacia colaboración.',
        href: '/oportunidades',
        completed: status.hasInterestedInvestors,
        icon: <Group />,
        ctaLabel: 'Gestionar',
      },
      {
        id: 'managed_investments',
        title: 'Revisa inversiones en curso',
        description: 'Filtra inversiones por fase, provincia o inversor.',
        href: '/inversiones',
        completed: status.hasInvestments,
        icon: <Checklist />,
        ctaLabel: 'Ver inversiones',
      },
      {
        id: 'itinerary',
        title: 'Abre un itinerario',
        description: 'Sigue tareas y fases de una inversión desde el checklist dedicado.',
        href: '/inversiones',
        completed: status.hasChecklist,
        icon: <Checklist />,
        ctaLabel: 'Ver itinerarios',
      },
      {
        id: 'contracts',
        title: 'Firma contratos',
        description: 'Controla contratos pendientes o firmados desde tus inversiones.',
        href: '/inversiones',
        completed: status.hasSignedContract,
        icon: <Description />,
        ctaLabel: 'Ver inversiones',
      },
      {
        id: 'investors',
        title: 'Consulta tus relaciones',
        description: 'Accede a tus inversores activos dentro de cada inversión.',
        href: '/inversiones',
        completed: status.hasCollaborations,
        icon: <Group />,
        ctaLabel: 'Ver inversiones',
      },
      {
        id: 'profile',
        title: 'Completa tus datos de contratación',
        description: 'Elige persona física o empresa y completa los datos para firmar contratos.',
        href: '/perfil?tab=contratacion',
        completed: contracting?.readyToContract ?? false,
        icon: <AccountCircle />,
        ctaLabel: 'Editar perfil',
      },
    ];
  }, [status, user, contracting?.readyToContract]);
}
